// PDFテキスト抽出(pdfjs-dist)。SPEC.md 7章の雛形をベースに実装。
// 座標ベースで空白/改行を判定してから正規化することで、日本語PDFの「1文字ごとの空白」
// (例: 「日 薬 情 発 第 8 7 号」)を「日薬情発第87号」に戻す。
// pdfjs-dist は資料作成画面でしか使わない重量級ライブラリ(バンドル1MB超)のため、
// 動的importで遅延ロードする(各社ポータルなど他画面の初期バンドルを膨らませないため)。
let pdfjsPromise: Promise<typeof import("pdfjs-dist")> | null = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
    ]).then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

/** 日本語PDFの文字間空白を除去。CJKが絡む空白のみ削除し、通常の英単語間スペースは残す。 */
export function normalizeJP(s: string): string {
  const isCJK = (c: string) => /[぀-ヿ㐀-鿿豈-﫿＀-￯　-〿]/.test(c);
  const chars = [...(s || "")];
  let out = "";
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i];
    if (c === " " || c === "\t" || c === "　") {
      const prev = out.slice(-1);
      let j = i + 1;
      while (j < chars.length && /[ \t　]/.test(chars[j])) j++;
      const next = chars[j] || "";
      if (prev && next && (isCJK(prev) || isCJK(next))) continue; // CJKが絡む空白は削除
      out += " ";
    } else {
      out += c;
    }
  }
  return out
    .replace(/(\d)\s+(?=\d)/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export interface ExtractResult {
  text: string;
  pages: number;
  scanned: boolean; // true: テキストがほぼ抽出できず、OCRへのフォールバックが必要
}

export async function extractPdfText(file: File): Promise<ExtractResult> {
  const pdfjs = await loadPdfjs();
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const out: string[] = [];

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    let line = "";
    let prevY: number | null = null;
    let prevEnd: number | null = null;
    let h = 10;
    const parts: string[] = [];

    for (const item of content.items as { str?: string; hasEOL?: boolean; transform: number[]; width?: number }[]) {
      if (!item.str) {
        if (item.hasEOL) {
          parts.push(line);
          line = "";
          prevEnd = null;
        }
        continue;
      }
      const x = item.transform[4];
      const y = item.transform[5];
      const w = item.width || 0;
      h = Math.abs(item.transform[3]) || h;
      if (prevY !== null && Math.abs(y - prevY) > h * 0.6) {
        parts.push(line);
        line = "";
        prevEnd = null;
      } else if (prevEnd !== null && x - prevEnd > h * 0.4) {
        line += " ";
      }
      line += item.str;
      prevEnd = x + w;
      prevY = y;
    }
    if (line) parts.push(line);
    out.push(parts.join("\n"));
  }

  const text = normalizeJP(out.join("\n"));
  return { text, pages: pdf.numPages, scanned: text.replace(/\s/g, "").length < 10 };
}

export async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
